import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  useTheme,
  alpha,
  TextField,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBack from "@mui/icons-material/ArrowBack";
import List from "@mui/icons-material/List";
import Edit from "@mui/icons-material/Edit";
import ToggleOn from "@mui/icons-material/ToggleOn";
import ToggleOff from "@mui/icons-material/ToggleOff";
import {
  useGetFeatureDetail,
  useUpdateFeature,
} from "../../hooks/api/useFeature";
import type { Feature } from "../../Graphql/types/feature";
import { ROUTE_MEMBERSHIP } from "../../constants/routes";
import { responsiveHeading } from "../../styles/layoutStyles";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return iso;
  }
}

function DetailItem({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? "0.875rem" : undefined,
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  );
}

const FeatureDetailView: React.FC = () => {
  const { featureCode } = useParams<{ featureCode: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [feature, setFeature] = useState<Feature | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmStatusOpen, setConfirmStatusOpen] = useState(false);
  const [pendingActive, setPendingActive] = useState<boolean | null>(null);

  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const getFeatureDetail = useGetFeatureDetail();
  const updateFeature = useUpdateFeature();

  useEffect(() => {
    if (!featureCode) return;
    getFeatureDetail
      .mutateAsync({ featureCode: decodeURIComponent(featureCode) })
      .then((res) => {
        const f = res.getFeature?.feature ?? null;
        setFeature(f);
        if (f) {
          setEditName(f.name);
          setEditDescription(f.description ?? "");
        }
      })
      .catch(() => {});
  }, [featureCode]);

  const openEdit = () => {
    if (feature) {
      setEditName(feature.name);
      setEditDescription(feature.description ?? "");
      setEditOpen(true);
    }
  };

  const handleSaveEdit = () => {
    if (!featureCode) return;
    const code = decodeURIComponent(featureCode);
    updateFeature
      .mutateAsync({
        featureCode: code,
        input: {
          name: editName,
          description: editDescription || undefined,
        },
      })
      .then((res) => {
        const updated = res.updateFeature?.feature;
        if (updated) setFeature(updated);
        setEditOpen(false);
      })
      .catch(() => {});
  };

  const pendingStatus = pendingActive;

  const openConfirmStatus = () => {
    if (!feature) return;
    setPendingActive(!feature.isActive);
    setConfirmStatusOpen(true);
  };

  const handleConfirmStatus = () => {
    if (!featureCode || pendingStatus == null) return;
    const code = decodeURIComponent(featureCode);
    updateFeature
      .mutateAsync({ featureCode: code, input: { isActive: pendingStatus } })
      .then((res) => {
        const updated = res.updateFeature?.feature;
        if (updated) setFeature(updated);
        setConfirmStatusOpen(false);
        setPendingActive(null);
      })
      .catch(() => {});
  };

  const handleCloseConfirmStatus = () => {
    setConfirmStatusOpen(false);
    setPendingActive(null);
  };

  if (!featureCode) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">Missing feature code.</Typography>
      </Box>
    );
  }

  if (!getFeatureDetail.isPending && !feature) {
    return (
      <Box sx={{ width: "100%" }}>
        <Typography color="text.secondary">Feature not found.</Typography>
        <Button
          startIcon={<ArrowBack />}
          sx={{ mt: 1 }}
          onClick={() => navigate(ROUTE_MEMBERSHIP)}
        >
          Back to membership
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <IconButton
        onClick={() => navigate(ROUTE_MEMBERSHIP)}
        sx={{ mb: 2, color: "text.secondary" }}
        aria-label="Back to membership"
      >
        <ArrowBack />
      </IconButton>

      {feature && (
        <>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${theme.palette.background.paper} 100%)`,
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.04)",
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              flexWrap="wrap"
              justifyContent="space-between"
            >
              <Box
                display="flex"
                alignItems="center"
                gap={2}
                minWidth={0}
                flex={1}
              >
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: "primary.main",
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <List sx={{ fontSize: 32 }} />
                </Box>
                <Box minWidth={0}>
                  <Typography
                    variant="h5"
                    sx={{ ...responsiveHeading, mb: 0.5 }}
                  >
                    {feature.name}
                  </Typography>
                  <Chip
                    label={feature.featureCode}
                    size="small"
                    sx={{ fontFamily: "monospace", fontWeight: 600 }}
                  />
                  <Chip
                    label={feature.isActive ? "Active" : "Inactive"}
                    size="small"
                    color={feature.isActive ? "success" : "default"}
                    sx={{ ml: 1, fontWeight: 600, textTransform: "uppercase" }}
                  />
                </Box>
              </Box>

              <Box
                display="flex"
                gap={1.5}
                flexWrap="wrap"
                justifyContent="flex-end"
                sx={{ flexShrink: 0 }}
              >
                <Button
                  variant="contained"
                  size="medium"
                  color={feature.isActive ? "warning" : "primary"}
                  startIcon={feature.isActive ? <ToggleOff /> : <ToggleOn />}
                  onClick={openConfirmStatus}
                  disabled={updateFeature.isPending}
                >
                  {feature.isActive ? "Deactivate" : "Activate"}
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<Edit />}
                  onClick={openEdit}
                >
                  Edit
                </Button>
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              boxShadow: "0 2px 12px rgba(0, 0, 0, 0.06)",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              Feature details
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
              gap={3}
            >
              <DetailItem
                label="Feature code"
                value={feature.featureCode}
                mono
              />
              <DetailItem label="Name" value={feature.name} />
              <Box sx={{ gridColumn: { xs: 1, sm: "1 / -1" } }}>
                <DetailItem
                  label="Description"
                  value={feature.description ?? "—"}
                />
              </Box>
              <DetailItem
                label="Created"
                value={formatDate(feature.createdAt)}
              />
              <DetailItem
                label="Updated"
                value={formatDate(feature.updatedAt)}
              />
            </Box>
          </Paper>
        </>
      )}

      <Dialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Edit feature</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              size="small"
            />
            <TextField
              label="Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveEdit}
            disabled={updateFeature.isPending}
          >
            {updateFeature.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmStatusOpen}
        onClose={handleCloseConfirmStatus}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {pendingStatus ? "Activate feature?" : "Deactivate feature?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {feature &&
              (pendingStatus
                ? `Are you sure you want to activate "${feature.name}"?`
                : `Are you sure you want to deactivate "${feature.name}"?`)}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseConfirmStatus} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color={pendingStatus ? "primary" : "warning"}
            onClick={handleConfirmStatus}
            disabled={updateFeature.isPending}
          >
            {updateFeature.isPending
              ? "Updating…"
              : pendingStatus
                ? "Activate"
                : "Deactivate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeatureDetailView;
